<template>
  <div v-if="_g_ShowStockFinderFilter" v-legacy-overlay class="legacy-finder finder-overlay" :class="_g_ThemeName" @click.self="close" @keydown="onKeydown">
    <section id="stocks-finder-modal" ref="dialog" class="finder-modal" role="dialog" aria-modal="true" aria-labelledby="legacy-finder-title" tabindex="-1" :aria-busy="busy">
      <header class="finder-header">
        <div><h3 id="legacy-finder-title">Filtros de acciones</h3><p>Prepara la búsqueda y pulsa Aplicar filtros.</p></div>
        <button class="finder-icon-button" aria-label="Cerrar filtros" :disabled="saving" data-usage-surface="filters" data-usage-action="filters-close" @click="close"><i class="material-icons">close</i></button>
      </header>
      <div class="finder-top-controls">
        <button class="finder-button" :disabled="busy || !canFilter" data-usage-surface="filters" data-usage-action="filters-reset" @click="load(true)"><i class="material-icons">refresh</i>Reiniciar</button>
        <div class="save-search-section">
          <button v-if="!showSave" class="finder-button" :disabled="busy || !draft" data-usage-surface="filters" data-usage-action="search-save-open" @click="showSave = true"><i class="material-icons">save</i>Guardar búsqueda</button>
          <form v-else class="add-search-form" @submit.prevent="saveNamed">
            <input v-model="searchName" class="vanilla-input" aria-label="Nombre de la búsqueda" placeholder="Nombre de la búsqueda" maxlength="80" :disabled="busy">
            <button class="finder-button" :disabled="busy || !searchName.trim()">Guardar</button>
            <button type="button" class="finder-icon-button" aria-label="Cancelar guardado" :disabled="busy" @click="showSave = false"><i class="material-icons">close</i></button>
          </form>
        </div>
      </div>
      <p v-if="collectionContext" class="finder-context">Estos filtros buscan en el mercado general. Al aplicarlos saldrás del listado actual; no se modificará su contenido.</p>
      <p v-if="!canFilter" class="finder-status" role="status">Tu plan no permite utilizar los filtros.</p>
      <div v-else-if="loading" class="finder-status" role="status">Cargando filtros…</div>
      <div v-else-if="draft" class="finder-scroll">
      <fieldset id="filters-user" class="filters-content" :disabled="saving">
        <First :finder-value="draft" :filters_user_json="permissions" @updateLastSearch="change" />
        <Second :finder-value="draft" :filters_user_json="permissions" @updateLastSearch="change" />
        <Third :finder-value="draft" :filters_user_json="permissions" @updateLastSearch="change" />
        <Fourth :finder-value="draft" :filters_user_json="permissions" @updateLastSearch="change" />
        <Fifth v-if="_g_User.roles[20] || _g_User.roles[21]" :finder-value="draft" @updateLastSearch="change" />
      </fieldset>
      </div>
      <div v-if="_g_User.has_cryptos_enabled && canFilter" class="finder-market">
        <label for="legacy-finder-market">Mercado</label>
        <select id="legacy-finder-market" v-model="draftCrypto" :disabled="busy" class="vanilla-select">
          <option :value="false">Acciones</option><option :value="true">Criptomonedas</option>
        </select>
      </div>
      <div v-if="error" class="finder-error" role="alert">{{ error }} <button v-if="!draft" class="finder-button" @click="load()">Reintentar</button></div>
      <p v-if="message" class="finder-message" role="status">{{ message }}</p>
      <footer class="finder-footer">
        <span class="finder-hint">{{ dirty ? 'Cambios pendientes de aplicar' : 'Cerrar no modifica la búsqueda' }}</span>
        <button class="finder-button" :disabled="saving" data-usage-surface="filters" data-usage-action="filters-close" @click="close">Cancelar</button>
        <button class="finder-button finder-primary" data-testid="apply-legacy-filters" :disabled="busy || !draft || !canFilter" data-usage-surface="filters" data-usage-action="filters-apply" @click="apply">{{ saving ? 'Aplicando…' : 'Aplicar filtros' }}</button>
      </footer>
    </section>
  </div>
</template>

<script>
import { legacyQueryConsumer } from "@/features/stockchart/models/legacyQuerySelection";
import { legacyComponentUsage } from '@/features/chart-usage/adapters/legacyComponentUsage';
import { mapGetters, mapMutations } from 'vuex';
import legacyOverlay from '../../directives/legacyOverlay';
import First from './First.vue';
import Second from './Second.vue';
import Third from './Third.vue';
import Fourth from './Fourth.vue';
import Fifth from './Fifth.vue';
import { copyFinder, patchFinder, validateFinder } from '../../models/legacyFinderDraft';
import { legacyCollectionStatus } from '../../models/legacyCollectionStatus';
import { loadLegacyFinder, saveLegacyFinder, saveLegacyNamedFinder, loadLegacyFinderStocks } from '../../api/legacyFinderRepository';

export default {
  mixins: [legacyQueryConsumer, legacyComponentUsage('filters')],
  name: 'FinderBase',
  directives: { legacyOverlay },
  components: { First, Second, Third, Fourth, Fifth },
  data: () => ({ draft: null, baseline: null, draftCrypto: false, baselineCrypto: false, loading: false, saving: false, error: '', message: '', showSave: false, searchName: '', requestId: 0, returnFocus: null }),
  computed: {
    ...mapGetters(['_g_ThemeName', '_g_ShowStockFinderFilter', '_g_User', '_g_UserFeatures', '_g_IsUserLogged', '_g_crypto_enable', '_g_SelectedTicker']),
    busy() { return this.loading || this.saving; },
    canFilter() { return this._g_IsUserLogged && this._g_UserFeatures.has_active_role && !this._g_User.is_only_authenticated_user; },
    permissions() { const value = this._g_User.permissions || {}; return value.permissions || value; },
    collectionContext() { return Boolean(this.$route.params.param1); },
    dirty() { return JSON.stringify(this.draft) !== JSON.stringify(this.baseline) || this.draftCrypto !== this.baselineCrypto; }
  },
  watch: {
    _g_ShowStockFinderFilter: {
      immediate: true,
      handler(open) {
        ++this.requestId;
        if (open) {
          this.returnFocus = document.activeElement;
          this.draft = null; this.baseline = null; this.showSave = false; this.searchName = '';
          this.draftCrypto = this.baselineCrypto = Boolean(this._g_crypto_enable);
          this.load();
          this.$nextTick(() => this.$refs.dialog && this.$refs.dialog.focus());
        }
      }
    }
  },
  beforeDestroy() { ++this.requestId; },
  methods: {
    ...mapMutations(['setShowStockFinderFilters', 'setFinderFilters', 'setStocks', 'setSelectedTicker', 'setCrypto', 'setIsLoadingStocks']),
    async load(defaults = false) {
      if (!this.canFilter || this.saving) return;
      const id = ++this.requestId;
      this.loading = true; this.error = ''; this.message = '';
      try {
        const value = await loadLegacyFinder(defaults);
        if (id !== this.requestId) return;
        this.draft = copyFinder(value);
        if (!defaults) this.baseline = copyFinder(value);
        else this.message = 'Valores predeterminados preparados. Pulsa Aplicar filtros para utilizarlos.';
      } catch (_) {
        if (id === this.requestId) this.error = 'No se pudieron cargar los filtros. Puedes reintentarlo.';
      } finally { if (id === this.requestId) this.loading = false; }
    },
    change(changes) { this.draft = patchFinder(this.draft, changes); this.error = ''; this.message = ''; },
    close() {
      if (this.saving) return;
      ++this.requestId;
      if (this._g_ShowStockFinderFilter) this.setShowStockFinderFilters();
      this.$nextTick(() => { if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus(); });
    },
    onKeydown(event) {
      if (this.$el.querySelector('.multi-select-overlay')) return;
      if (event.key === 'Escape') { event.stopPropagation(); this.close(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(this.$refs.dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href]')).filter(el => el.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) { event.preventDefault(); if (last) last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); if (first) first.focus(); }
    },
    async apply() {
      if (this.busy || !this.draft || !this.canFilter) return;
      this.error = validateFinder(this.draft);
      if (this.error) return;
      this.saving = true; this.message = '';
      let saved = false;
      const query = this.legacyQuery, token = query && query.capture();
      const current = () => !this._isDestroyed && (!query || query.isCurrent(token));
      try {
        await saveLegacyFinder(this.draft, this.draftCrypto);
        if (!current()) return;
        saved = true;
        this.setFinderFilters(copyFinder(this.draft));
        if (this.draftCrypto !== Boolean(this._g_crypto_enable)) this.setCrypto();
        if (this.collectionContext) {
          window.sessionStorage.removeItem('ticker_search_by_user');
          this.saving = false; this.close();
          await this.$router.push('/app/graficador-dinamico');
        } else {
          const stocks = await loadLegacyFinderStocks();
          if (!current()) return;
          legacyCollectionStatus.error = '';
          this.setStocks(stocks);
          if (this.legacyQuery) this.legacyQuery.select('finder');
          this.setSelectedTicker(stocks.find(stock => stock.ticker === this._g_SelectedTicker.ticker) || stocks[0] || {});
          this.setIsLoadingStocks(false);
          this.saving = false; this.close();
          this.$vueOnToast.pop('success', 'Filtros aplicados', `${stocks.length} valores encontrados.`);
        }
      } catch (_) {
        if (!current()) return;
        this.error = saved ? 'Los filtros se han guardado, pero no se pudieron cargar los resultados. Pulsa Aplicar filtros para reintentar.' : 'No se pudieron aplicar los filtros. Conservamos tus cambios para que puedas reintentarlo.';
      } finally { this.saving = false; }
    },
    async saveNamed() {
      if (this.busy || !this.searchName.trim() || !this.draft) return;
      this.error = validateFinder(this.draft);
      if (this.error) return;
      this.saving = true;
      try {
        await saveLegacyNamedFinder(this.searchName, this.draft);
        this.message = 'Búsqueda guardada. Pulsa Aplicar filtros si quieres utilizarla ahora.';
        this.showSave = false; this.searchName = '';
      } catch (_) { this.error = 'No se pudo guardar la búsqueda. Puedes reintentarlo.'; }
      finally { this.saving = false; }
    }
  }
};
</script>

<style lang="scss">
@import '../../styles/legacy-finder.scss';
</style>
